import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(loginData.email, loginData.password);
      toast.success('Login successful!');
      // Division secretaries land on the portal; everyone else hits /dashboard.
      if (data?.user?.role === 'division_secretary') {
        navigate('/portal');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex relative bg-[#00204F]"
      style={{
        backgroundImage: 'url(/brand/mpl-banner.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Full-page dark navy overlay for readability */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, rgba(0,20,55,0.96) 0%, rgba(0,32,79,0.90) 45%, rgba(0,32,79,0.75) 75%, rgba(255,127,0,0.18) 100%)',
        }}
      />

      {/* Left side - MPL Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        {/* Decorative orange vertical accent */}
        <div
          className="absolute top-0 right-0 h-full w-1"
          style={{ background: 'linear-gradient(180deg, #FF7F00 0%, transparent 100%)' }}
        />

        <div className="relative z-10 flex flex-col justify-between px-12 py-10 text-white w-full">
          {/* Top badge: MPL logo + league name */}
          <div className="flex items-center gap-4">
            <img
              src="/brand/mpl-logo.png"
              alt="Madhya Pradesh League — Scindia Cup"
              className="h-20 w-20 object-contain drop-shadow-lg"
            />
            <div>
              <div className="text-[11px] font-semibold tracking-[0.25em] text-[#FF7F00] uppercase">
                Madhya Pradesh League
              </div>
              <div className="text-sm text-white/80 tracking-wide">
                for the Scindia Cup · T20
              </div>
            </div>
          </div>

          {/* Center headline */}
          <div className="space-y-5 max-w-xl">
            <div
              className="inline-block text-[11px] font-semibold tracking-[0.25em] uppercase px-3 py-1 rounded-sm"
              style={{ background: 'rgba(255,127,0,0.15)', color: '#FFB266', border: '1px solid rgba(255,127,0,0.35)' }}
            >
              AI-Powered Finance Console
            </div>
            <h1 className="text-5xl xl:text-6xl font-heading font-bold tracking-tight leading-[1.05]">
              Run the league.
              <br />
              <span style={{ color: '#FF7F00' }}>Own the numbers.</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed">
              An AI-powered finance console for the national cricketing environment — purpose-built
              to handle every type of financial decision, end to end.
            </p>
          </div>

          {/* Bottom attribution */}
          <div className="flex items-center gap-3 text-xs text-white/60">
            <img
              src="/brand/gdca-logo.jpg"
              alt="GDCA"
              className="h-10 w-10 object-contain rounded-sm bg-white/90 p-1"
            />
            <div className="leading-tight">
              <div className="text-white/80 font-medium">Gwalior Division Cricket Association</div>
              <div>Under aegis of MPCA &amp; BCCI</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Auth Forms (glass card over shared cricket bg) */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 relative z-10">
        <Card
          className="w-full max-w-md border-white/10 shadow-2xl"
          style={{
            background: 'rgba(10, 22, 48, 0.72)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
          }}
        >
          <CardHeader className="space-y-3">
            {/* Compact mobile / small-screen brand */}
            <div className="flex items-center gap-3 lg:hidden">
              <img src="/brand/mpl-logo.png" alt="MPL" className="h-10 w-10 object-contain" />
              <div>
                <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#FF7F00]">
                  Madhya Pradesh League
                </div>
                <div className="text-xs text-white/60">Finance Console</div>
              </div>
            </div>
            <CardTitle className="text-2xl font-heading text-white">Welcome back</CardTitle>
            <CardDescription className="text-white/70">
              Sign in to the MPL Finance Console
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-white/80">Email</Label>
                    <Input
                      data-testid="login-email-input"
                      id="login-email"
                      type="email"
                      placeholder="you@mpleaguet20.com"
                      value={loginData.email}
                      onChange={(e) =>
                        setLoginData({ ...loginData, email: e.target.value })
                      }
                      required
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#FF7F00]/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-white/80">Password</Label>
                    <Input
                      data-testid="login-password-input"
                      id="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) =>
                        setLoginData({ ...loginData, password: e.target.value })
                      }
                      required
                      className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#FF7F00]/40"
                    />
                  </div>
                  <Button
                    data-testid="login-submit-button"
                    type="submit"
                    className="w-full text-white font-semibold shadow-lg transition-all hover:brightness-110"
                    style={{ background: 'linear-gradient(90deg, #FF7F00 0%, #E56A00 100%)' }}
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
